import React from 'react';
import * as Sentry from '@sentry/browser';

import { useTranslation } from 'react-i18next';
import { Modal, Button, Message, Table, ToastContext } from '@weezevent/nacre';
import useAppContext from '../../../context/useAppContext';
import AttendeeModel from '../../../models/attendeeModel';
import OrderModel from '../models/orderModel';
import { AttendeeStatus } from '../../attendee/list/components/attendee-row/attendee-row';

function RefundRequestRow({ refundRequest, selected, attendeeRelated, onChange }) {
    const { t } = useTranslation();

    const attendeeOutput = React.useMemo(() => {
        return (
            <div>
                {attendeeRelated.last_name} {attendeeRelated.first_name} {<AttendeeStatus attendee={attendeeRelated} />}
            </div>
        );
    }, [attendeeRelated]);

    const madeBy = React.useMemo(() => {
        if (refundRequest.account) {
            return `${refundRequest.account.first_name} ${refundRequest.account.last_name}`;
        }
        return t('ticket.manager.common.action.delete_refund_requests.madeByWidget', { widget: refundRequest.origin });
    }, [refundRequest]);

    return (
        <Table.Tr active={selected} onSelected={(event, data) => onChange(refundRequest.id, data.checked)}>
            <Table.Td>{refundRequest.amount}</Table.Td>
            <Table.Td>{refundRequest.type}</Table.Td>
            <Table.Td>{attendeeOutput}</Table.Td>
            <Table.Td>{madeBy}</Table.Td>
        </Table.Tr>
    );
}

function RefundRequestTable({ tableData, setSelectedRefundRequest, selectedRefundRequest }) {
    const { t } = useTranslation();
    const [allSelected, setAllSelected] = React.useState(false);

    const allRefundIds = React.useMemo(() => {
        return tableData.map(data => data.refundRequest.id);
    }, [tableData]);

    const onSelectAll = React.useCallback(
        checked => {
            setSelectedRefundRequest(checked ? allRefundIds : []);
            setAllSelected(checked);
        },
        [allRefundIds]
    );

    const onChangeRow = React.useCallback(
        (id, checked = true) => {
            if (checked) {
                selectedRefundRequest.push(id);
                setSelectedRefundRequest([...selectedRefundRequest]);
                setAllSelected(allRefundIds.length === selectedRefundRequest.length);
            }

            if (!checked) {
                let copy_selectedRefundRequest = [...selectedRefundRequest];
                let refundRequestIndex = copy_selectedRefundRequest.findIndex(refundIds => refundIds === id);
                copy_selectedRefundRequest.splice(refundRequestIndex, 1);
                setSelectedRefundRequest([...copy_selectedRefundRequest]);
                setAllSelected(allRefundIds.length === copy_selectedRefundRequest.length);
            }
        },
        [selectedRefundRequest, allRefundIds]
    );

    const tableHeader = React.useMemo(() => {
        return (
            <Table.THead>
                <Table.Tr active={allSelected}>
                    <th>{t('ticket.refund_request_modal.table_header.refund_request_amount')}</th>
                    <th>{t('ticket.refund_request_modal.table_header.refund_request_type')}</th>
                    <th>{t('ticket.refund_request_modal.table_header.attendee_related')}</th>
                    <th>{t('ticket.refund_request_modal.table_header.origin')}</th>
                </Table.Tr>
            </Table.THead>
        );
    }, [allSelected]);

    if (!tableData.length) {
        return null;
    }

    return (
        <Table useNew selectable onSelectAll={(event, data) => onSelectAll(data.checked)}>
            {tableHeader}
            <Table.TBody>
                {tableData.map(data => {
                    return (
                        <RefundRequestRow
                            key={data['refundRequest'].id}
                            refundRequest={data['refundRequest']}
                            selected={selectedRefundRequest.includes(data['refundRequest'].id)}
                            attendeeRelated={data['attendeeRelated']}
                            onChange={onChangeRow}
                        />
                    );
                })}
            </Table.TBody>
        </Table>
    );
}

export default function DeleteRefundRequestPostAction({ actionInstance, onClose }) {
    const { t } = useTranslation();
    const toast = React.useContext(ToastContext);
    const [selectedRefundRequest, setSelectedRefundRequest] = React.useState([]);

    const [{ event, organization }] = useAppContext();
    const [isSubmitting, setIsSubmitting] = React.useState(false);

    const _filtered_orders = React.useMemo(() => {
        return actionInstance.filtered_items.filter(obj => obj.refund_requests && obj.refund_requests.length !== 0);
    }, [actionInstance]);

    const handleSubmit = React.useCallback(() => {
        setIsSubmitting(true);
        actionInstance
            .runPostAction({ requests_to_delete: selectedRefundRequest })
            .then(response => {
                toast.success(t('ticket.manager.common.action.' + actionInstance.constructor.NAME + '.postAction.toast.success', { count: selectedRefundRequest.length }));
                setIsSubmitting(false);
                onClose();
            })
            .catch(e => {
                console.error(e);
                Sentry.captureException(e);
                toast.error(t('ticket.manager.common.action.' + actionInstance.constructor.NAME + '.postAction.toast.error', { count: selectedRefundRequest.length }));
                setIsSubmitting(false);
            });
    }, [actionInstance, setIsSubmitting, onClose, event, organization, selectedRefundRequest]);

    const _onClose = React.useCallback(() => {
        onClose();
        setIsSubmitting(false);
    }, [onClose]);

    const tableData = React.useMemo(() => {
        let data = [];
        actionInstance.filtered_items.forEach(obj => {
            if (obj instanceof AttendeeModel) {
                obj.refund_requests
                    .filter(refundRequest => refundRequest.removed === null)
                    .forEach(refundRequest => {
                        data.push({
                            refundRequest: refundRequest,
                            attendeeRelated: obj
                        });
                    });
            }

            if (obj instanceof OrderModel) {
                obj.refund_requests
                    .filter(refundRequest => refundRequest.removed === null)
                    .forEach(refundRequest => {
                        let attendeeRelated = obj.attendees.find(attendee => attendee.id === refundRequest.attendee);
                        attendeeRelated.order = obj;
                        data.push({
                            refundRequest: refundRequest,
                            attendeeRelated: attendeeRelated
                        });
                    });
            }
        });
        return data;
    }, [actionInstance]);

    //0 orders to send
    React.useEffect(() => {
        if (Array.isArray(_filtered_orders) && !_filtered_orders.length) {
            onClose();
        }
    }, [_filtered_orders]);

    return (
        <Modal
            open={Boolean(actionInstance && Array.isArray(_filtered_orders) && _filtered_orders.length)}
            onClose={_onClose}
            size={'large'}
            title={t('ticket.manager.common.action.' + actionInstance.constructor.NAME + '.postAction.title', { count: _filtered_orders.length })}
            submitButton={<Button primary onClick={handleSubmit} disabled={isSubmitting} isLoading={isSubmitting} label={t('ticket.common.action.confirm')} />}
            cancelButton={<Button onClick={_onClose} label={t(`ticket.common.action.cancel`)} />}
        >
            <Message type={'default'}>
                {t('ticket.manager.common.action.' + actionInstance.constructor.NAME + '.postAction.information', { count: actionInstance.filtered_items.length })}
            </Message>
            <div style={{ padding: '15px' }}>
                <RefundRequestTable tableData={tableData} setSelectedRefundRequest={setSelectedRefundRequest} selectedRefundRequest={selectedRefundRequest} />
            </div>
        </Modal>
    );
}
